import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import ReactTable from "react-table";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import matchSorter from "match-sorter";

import { downloadCSV } from "../utils";

import { customToast as toast } from "../../../utils";

import * as contractActions from "../../../actions/contractActions";
import * as exportActions from "../../../actions/exportActions";

import Loading from "../../common/Loading";
import HeaderWrapper from "../../common/HeaderWrapper";
import { CONTRACT_COLUMNS } from "./columns";

const BillableCustomerRates = ({
  contracts,
  actions,
  isMobile,
  exporting,
  exported,
  codes
}) => {
  const activeCode = useMemo(
    () =>
      codes.filter(
        (code) =>
          code.entity === "Contract" &&
          code.field === "status" &&
          code.label === "Active"
      ),
    [codes]
  );

  const [state, setState] = useState({
    defaultPageSize: localStorage.getItem("DEFAULT_PAGE_SIZE_CONTRACT_LIST")
      ? parseInt(localStorage.getItem("DEFAULT_PAGE_SIZE_CONTRACT_LIST"))
      : 10,
    page: 0,
    sorted: [{ id: "name", desc: false }],
    filtered: []
  });

  useEffect(() => {
    actions.loadContracts({
      filtered: {
        billable: true,
        contract_status: [activeCode?.[0]?.id]
      }
    });
  }, [actions, activeCode]);

  useEffect(() => {
    setState((prev) => ({ ...prev, filtered: contracts }));
  }, [contracts]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Billable-Customer-Rates-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const onPageSizeChange = (pageSize) => {
    localStorage.setItem("DEFAULT_PAGE_SIZE_CONTRACT_LIST", pageSize);
    setState((prev) => ({ ...prev, defaultPageSize: pageSize }));
  };

  const onPageChange = (currentPage) => {
    setState((prev) => ({ ...prev, page: currentPage }));
  };

  const onSortedChange = (newSorted) => {
    setState((prev) => ({ ...prev, sorted: newSorted }));
  };

  const onSearch = (text) => {
    const filtered =
      text === ""
        ? contracts
        : matchSorter(contracts, text, {
            keys: ["name"]
          });
    setState((prev) => ({
      ...prev,
      filtered
    }));
  };

  const customTableSort = (a, b, isDesc) => {
    if (typeof a === "number" && typeof b === "number") {
      if (a < b) {
        return -1 * isDesc;
      }
      if (a > b) {
        return 1 * isDesc;
      }
      return 0;
    }
    if ((typeof a === "string" && typeof b === "string") || !a || !b) {
      const nameA = !a ? "" : a?.toUpperCase()?.replaceAll(" ", "");
      const nameB = !b ? "" : b?.toUpperCase()?.replaceAll(" ", "");

      if (nameA < nameB) {
        return -1 * isDesc;
      }
      if (nameA > nameB) {
        return 1 * isDesc;
      }
      return 0;
    }
    return 0;
  };

  const handleExportCSV = async () => {
    try {
      const name = "Billable Customer Rates Report";
      const sortName = state.sorted[0]?.id;
      const sortDesc = state.sorted[0]?.desc ? -1 : 1;

      const data = state.filtered
        .map((item) => ({
          ...item,
          service_note: item.service_note ? `"${item.service_note}"` : ""
        }))
        .sort((a, b) => customTableSort(a[sortName], b[sortName], sortDesc));
      await downloadCSV(name, data, CONTRACT_COLUMNS, name);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleExportPDF = () => {
    const queryFilters = {
      billable: true,
      contract_status: [activeCode?.[0]?.id],
      sort: state.sorted
    };
    const payload = {
      data: queryFilters,
      type: "BILLABLE_CUSTOMER_RATES",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  if (!contracts.length) {
    return (
      <div className="bcr-loading">
        <Loading />
      </div>
    );
  }

  const { length } = state.filtered;

  return (
    <React.Fragment>
      <div className="bcr-header">
        <HeaderWrapper
          breadcrumbs={[{ label: `Contracts (${length} contracts)` }]}
          onClickAction={[
            {
              action: () => handleExportCSV(),
              className: "btn-success",
              text: "CSV",
              disabled: !length || exporting
            },
            {
              action: () => handleExportPDF(),
              className: "btn-danger",
              text: "PDF",
              disabled: !length || exporting
            }
          ]}
          isMobile={isMobile}
          onSearch={onSearch}
        />
      </div>
      <div className="content-wrapper">
        <ReactTable
          className="-highlight"
          data={state.filtered}
          columns={CONTRACT_COLUMNS}
          pages={Math.ceil(length / state.defaultPageSize)}
          pageSize={state.defaultPageSize}
          page={state.page}
          sorted={state.sorted}
          onSortedChange={onSortedChange}
          showPagination={length > 5}
          showPageSizeOptions={!isMobile}
          noDataText="No contracts found"
          onPageSizeChange={onPageSizeChange}
          onPageChange={onPageChange}
          defaultSortMethod={(a, b) => customTableSort(a, b, 1)}
        />
      </div>
    </React.Fragment>
  );
};

BillableCustomerRates.propTypes = {
  contracts: PropTypes.array,
  actions: PropTypes.object,
  isMobile: PropTypes.bool,
  exporting: PropTypes.bool,
  exported: PropTypes.string,
  codes: PropTypes.array.isRequired
};

BillableCustomerRates.defaultProps = {
  contracts: [],
  actions: {},
  isMobile: false
};

function mapStatesToProps(state) {
  return {
    globalState: state.reducers,
    contracts: state.reducers.contracts,
    exporting: state.reducers.exporting,
    exported: state.reducers.exported,
    codes: state.reducers.codes
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...contractActions,
        ...exportActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(BillableCustomerRates);
